import React, { FC } from 'react';
import { messaging } from '@factorin/container.macro';
import fp from 'lodash/fp';

import { animation as defaultAnimation, cn, stopPropagation } from 'Modules/ui';
import { Box, CloseModalButton, Stack } from 'Modules/ui3';

import { useHideAction } from '../../state';
import styles from './index.module.scss';
import { ModalComponentProps, ModalProps } from './types';
import { ModalContextObject, closeButtonVariants } from './utils';


export * from './utils';

export const Modal: FC<ModalProps> = React.memo(({ modal }) => {
    const { type, props = {} } = modal;
    const { hideModal } = useHideAction();
    if (!messaging.modalTypes[type]) {
        if (process.env.NODE_ENV === 'development')
            console.log('no modal type registered', type);
        return null;
    }

    const {
        component: Component,
        containerClass,
        modalClass,
        width = 'auto',
        hPosition = 'center',
        vPosition = 'center',
        customCloseButton: CustomCloseButton,
        closeButtonVariant = 'default',
        animation = {},
        displayShadow,
    } = messaging.modalTypes[type] as ModalComponentProps;
    return (
        <Box
            position='fixed'
            top={0}
            right={0}
            bottom={0}
            left={0}
            zIndex={40}
            className={cn(hPosition === 'right' ? styles.modalRight : styles.modalCenter,
                modalClass)}
            role='presentation'
            {...modal.closeable && {
                onMouseDown: () => hideModal(modal),
            }}
            sx={{
                overflowX: 'hidden',
                overflowY: 'auto',
                backgroundColor: 'rgba(var(--bg-secondary-dark), 0.3)',
                ...hPosition === 'right' ? {
                    '& > div > div': {
                        flex: 1,
                        transitionProperty: 'right',
                        transitionDuration: `.${(animation.duration || defaultAnimation) / 10}s`,
                        transitionTimingFunction: animation.timingFunc || 'ease-out',
                    },
                } : {
                    transitionProperty: 'opacity',
                    transitionDuration: `.${(animation.duration || defaultAnimation * 0.5) / 10}s`,
                    transitionTimingFunction: animation.timingFunc || 'ease',
                },
            }}
        >
            <Stack
                justifyContent='center'
                className={hPosition === 'center' ? 'hcenter' : undefined}
                top={0}
                minHeight='100%'
                {...hPosition === 'right' && {
                    height: '100%',
                }}
                {...hPosition === 'center' && {
                    py: 8,
                }}
            >
                {vPosition === 'centerTop' && <Box minHeight='1.8rem' flexGrow={1} />}
                <Stack
                    data-testid={`modal-${type}`}
                    className={containerClass}
                    position='relative'
                    onMouseDown={stopPropagation}
                    width={fp.isFunction(width) ? width() : width}
                    minWidth='30rem'
                    maxHeight={hPosition === 'center' ? 'fit-content' : '100%'}
                    role='presentation'
                    {...hPosition === 'right' && {
                        ml: 'auto',
                        mr: 0,
                    }}
                    {...displayShadow && {
                        boxShadow: '0 0.5rem 2rem rgba(var(--bg-secondary-dark), 0.2)',
                    }}
                >
                    <ModalContextObject.Provider
                        value={{ modal: { ...modal, hide: () => hideModal(modal) } }}
                    >
                        <Component {...props} />
                    </ModalContextObject.Provider>
                    {modal.showCloseButton && (
                        CustomCloseButton
                            ? <CustomCloseButton onClick={() => hideModal(modal)} />
                            : CustomCloseButton !== null ? (
                                <CloseModalButton
                                    {...closeButtonVariants[closeButtonVariant]}
                                    onClick={() => hideModal(modal)}
                                />
                            ) : null
                    )}
                </Stack>
                {vPosition === 'centerTop' && <Box minHeight='1.8rem' flexGrow={2} />}
            </Stack>
        </Box>
    );
});
